import { supabase } from './supabase';

/**
 * Message interface for the messages table
 */
export interface Message {
  message_id: string;
  conversation_id: string;
  role: 'user' | 'model';
  prompt_text?: string | null;
  response_text?: string | null;
  content?: any[] | null; // JSONB array of message content parts (text, tool-calls, etc.)
  timestamp: string;
  input_tokens?: number | null;
  output_tokens?: number | null;
  tenant_id: string;
}

/**
 * Assistant-UI compatible message format
 */
export interface AssistantMessage {
  id: string;
  role: 'user' | 'assistant';
  content: Array<{
    type: 'text';
    text: string;
  }>;
  createdAt?: Date;
}

/**
 * Get all messages for a conversation
 * @param conversationId - Conversation ID
 * @returns Promise with array of messages
 */
export const getMessages = async (conversationId: string): Promise<Message[]> => {
  console.log('Fetching messages for conversation:', conversationId);

  try {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('timestamp', { ascending: true });

    if (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }

    console.log(`Fetched ${data?.length || 0} messages from database`);
    return data || [];
  } catch (error) {
    console.error('Error in getMessages:', error);
    throw error;
  }
};

/**
 * Convert database messages to assistant-ui format
 * @param messages - Array of database messages
 * @returns Array of assistant-ui compatible messages
 */
export const convertToAssistantMessages = (messages: Message[]): any[] => {
  return messages.map(message => {
    // 1. New "Full Object" format: message.content is the entire UIMessage object
    if (message.content && !Array.isArray(message.content) && (message.content as any).parts) {
      return message.content;
    }

    // 2. Mapping logic for legacy or intermediate formats
    let parts: any[] = [];
    if (message.content && Array.isArray(message.content)) {
      // Use the structured content (previously stored as the 'content' array)
      parts = message.content;
    } else {
      // Fall back to text-only content for backward compatibility
      let contentText = '';
      if (message.role === 'user') {
        contentText = message.prompt_text || '';
      } else if (message.role === 'model') {
        contentText = message.response_text || '';
      }

      parts = [{
        type: 'text' as const,
        text: contentText
      }];
    }

    return {
      id: message.message_id,
      role: message.role === 'model' ? 'assistant' : 'user',
      parts, // Modern AI SDK uses 'parts'
      createdAt: new Date(message.timestamp)
    };
  });
};

/**
 * Get messages for a conversation in assistant-ui format
 * @param conversationId - Conversation ID
 * @returns Promise with array of assistant-ui compatible messages
 */
export const getAssistantMessages = async (conversationId: string): Promise<any[]> => {
  try {
    console.log('[MessagesService] Fetching history for:', conversationId);
    const messages = await getMessages(conversationId);
    console.log(`[MessagesService] Found ${messages.length} raw messages`);
    const assistantMessages = convertToAssistantMessages(messages);
    console.log(`[MessagesService] Converted to ${assistantMessages.length} UI messages`);
    return assistantMessages;
  } catch (error) {
    console.error('[MessagesService] Error in getAssistantMessages:', error);
    throw error;
  }
};

/**
 * Create a new message in the database
 * @param conversationId - Conversation ID
 * @param role - Message role ('user' or 'model')
 * @param text - Message text content
 * @param isPrompt - Whether this is a prompt (user) or response (model)
 * @returns Promise with created message
 */
export const createMessage = async (
  conversationId: string,
  role: 'user' | 'model',
  text: string,
  isPrompt: boolean = role === 'user'
): Promise<Message> => {
  try {
    // Import getCurrentTenantId to get tenant context
    const { getCurrentTenantId } = await import('@/lib/auth-utils');
    const tenantId = await getCurrentTenantId();

    if (!tenantId) {
      throw new Error('Unable to determine tenant context');
    }

    const payload: any = {
      conversation_id: conversationId,
      role,
      timestamp: new Date().toISOString(),
      tenant_id: tenantId,
    };

    // Set the appropriate text field based on role
    if (isPrompt) {
      payload.prompt_text = text;
    } else {
      payload.response_text = text;
    }

    const { data, error } = await supabase
      .from('messages')
      .insert(payload)
      .select()
      .single();

    if (error) {
      console.error('Error creating message:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in createMessage:', error);
    throw error;
  }
};

/**
 * Create a new message with full content structure (including tool calls)
 * @param conversationId - Conversation ID
 * @param role - Message role ('user' or 'model')
 * @param content - Full message content array (text, tool-calls, etc.)
 * @returns Promise with created message
 */
export const createMessageWithContent = async (
  conversationId: string,
  role: 'user' | 'model',
  content: any[]
): Promise<Message> => {
  try {
    // Import getCurrentTenantId to get tenant context
    const { getCurrentTenantId } = await import('@/lib/auth-utils');
    const tenantId = await getCurrentTenantId();

    if (!tenantId) {
      throw new Error('Unable to determine tenant context');
    }

    // Extract text content for backward compatibility
    const textContent = content
      .filter(part => part.type === 'text')
      .map(part => part.text)
      .join('\n');

    const payload: any = {
      conversation_id: conversationId,
      role,
      content, // Store full content structure
      timestamp: new Date().toISOString(),
      tenant_id: tenantId,
    };

    // Also set text fields for backward compatibility
    if (role === 'user') {
      payload.prompt_text = textContent;
    } else {
      payload.response_text = textContent;
    }

    const { data, error } = await supabase
      .from('messages')
      .insert(payload)
      .select()
      .single();

    if (error) {
      console.error('Error creating message with content:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in createMessageWithContent:', error);
    throw error;
  }
};

/**
 * Update message tokens
 * @param messageId - Message ID
 * @param inputTokens - Input tokens count
 * @param outputTokens - Output tokens count
 * @returns Promise with updated message
 */
export const updateMessageTokens = async (
  messageId: string,
  inputTokens?: number,
  outputTokens?: number
): Promise<Message> => {
  try {
    const updateData: any = {};
    if (inputTokens !== undefined) updateData.input_tokens = inputTokens;
    if (outputTokens !== undefined) updateData.output_tokens = outputTokens;

    const { data, error } = await supabase
      .from('messages')
      .update(updateData)
      .eq('message_id', messageId)
      .select()
      .single();

    if (error) {
      console.error('Error updating message tokens:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in updateMessageTokens:', error);
    throw error;
  }
};

/**
 * Delete a message
 * @param messageId - Message ID
 * @returns Promise
 */
export const deleteMessage = async (messageId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('messages')
      .delete()
      .eq('message_id', messageId);

    if (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteMessage:', error);
    throw error;
  }
};

/**
 * Get message count for a conversation
 * @param conversationId - Conversation ID
 * @returns Promise with message count
 */
export const getMessageCount = async (conversationId: string): Promise<number> => {
  try {
    const { count, error } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('conversation_id', conversationId);

    if (error) {
      console.error('Error getting message count:', error);
      throw error;
    }

    return count || 0;
  } catch (error) {
    console.error('Error in getMessageCount:', error);
    throw error;
  }
};
